import { Helmet } from 'react-helmet-async';
import { Grid, Container, Tabs, Tab, Box, Typography, Chip, TextField, MenuItem, Stack, Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { motion } from 'framer-motion';
import AppWidgetSummary from '../sections/dashboard/app/AppWidgetSummary';
import React, { useEffect, useMemo, useState } from 'react';
import Chart from '../components/chart/Chart'
import axios from 'axios'

export default function DashboardAppPage() {
  const [metrics, setMetrics] = useState({ products: 0, orders: 0, byStatus: {}, pendingToday: 0 });
  const [engagement, setEngagement] = useState(null);
  const [tab, setTab] = useState(0);
  const [userStats, setUserStats] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [userPeriod, setUserPeriod] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [userCountry, setUserCountry] = useState('all');
  const [userRole, setUserRole] = useState('all');
  const [userActivity, setUserActivity] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchChartData = async (type) => {
    try {
      const r = await axios.get(`/api/v1/metrics/chart?type=${type}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('USER_TOKEN')}` }
      });
      if (r.data?.data) setChartData(r.data.data);
    } catch (e) { }
  };

  useEffect(() => {
    const load = async () => {
      try {
        // 1) Try fast summary endpoint
        const m = await axios.get('/api/v1/metrics/summary');
        const md = m.data?.data;
        if (md) {
          setMetrics({ products: md.productsTotal || 0, orders: md.ordersTotal || 0, byStatus: md.byStatus || {}, pendingToday: md.pendingToday || 0 });
          return;
        }
        // 2) Fallback: query list endpoints to derive totals
        const [prodRes, ordRes] = await Promise.all([
          axios.get('/api/v1/products', { params: { limit: 1 } }),
          axios.get('/api/v1/orders', { params: { limit: 1 } })
        ]);
        const prodPayload = prodRes.data?.data;
        const ordPayload = ordRes.data?.data;
        const productsTotal = prodPayload?.pagination?.total ?? (Array.isArray(prodPayload?.items) ? prodPayload.items.length : 0);
        const ordersTotal = ordPayload?.pagination?.total ?? (Array.isArray(ordPayload?.items) ? ordPayload.items.length : 0);

        const statuses = ['Pending','Processing','Shipped','Delivered','Cancelled'];
        const counts = {};
        await Promise.all(statuses.map(async s => {
          try {
            const r = await axios.get('/api/v1/orders', { params: { limit: 1, status: s } });
            const payload = r.data?.data;
            counts[s] = payload?.pagination?.total ?? (Array.isArray(payload?.items) ? payload.items.length : 0);
          } catch {
            counts[s] = 0;
          }
        }));

        // Fallback pendingToday (best-effort): query today's pending with date filters, if backend list supports from/to
        let pendingToday = 0;
        try {
          const start = new Date(); start.setHours(0,0,0,0);
          const end = new Date(); end.setHours(23,59,59,999);
          const r = await axios.get('/api/v1/orders', { params: { limit: 1, status: 'Pending', from: start.toISOString(), to: end.toISOString() } });
          const payload = r.data?.data;
          pendingToday = payload?.pagination?.total ?? 0;
        } catch { /* ignore */ }

        setMetrics({ products: productsTotal, orders: ordersTotal, byStatus: counts, pendingToday });
      } catch (e) {
        // swallow
      }
    };
    load();

    const loadEngagement = async () => {
      try {
        const r = await axios.get('/api/v1/metrics/engagement', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('USER_TOKEN')}` }
        });
        if (r.data?.data) setEngagement(r.data.data);
      } catch (e) { }
      
      try {
        const u = await axios.get('/api/v1/metrics/user-stats', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('USER_TOKEN')}` }
        });
        if (u.data?.data) setUserStats(u.data.data);
      } catch (e) { }
    };
    loadEngagement();
    fetchChartData('monthly');
  }, []);

  useEffect(() => {
    fetchChartData(tab === 0 ? 'daily' : 'monthly');
  }, [tab]);

  const tabData = engagement ? [engagement.daily, engagement.monthly, engagement.yearly] : [null, null, null];
  const currentData = tabData[tab];

  const filterOptions = useMemo(() => ({
    countries: [...new Set(userStats.map((user) => user.country).filter(Boolean))].sort(),
    roles: [...new Set(userStats.map((user) => user.gamingRole).filter(Boolean))].sort(),
  }), [userStats]);

  const filteredUserStats = useMemo(() => {
    const now = new Date();
    let from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    let to = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;
    if (!from && !to && userPeriod !== 'all' && userPeriod !== 'custom') {
      from = new Date(now);
      if (userPeriod === 'daily') from.setHours(0, 0, 0, 0);
      if (userPeriod === 'monthly') from = new Date(now.getFullYear(), now.getMonth(), 1);
      if (userPeriod === 'yearly') from = new Date(now.getFullYear(), 0, 1);
      to = now;
    }

    const search = userSearch.trim().toLowerCase();
    return userStats.filter((user) => {
      const searchable = [user.name, user.email, user.phone, user.trooperId,
        user.referralCode, user.country].join(' ').toLowerCase();
      const lastActive = user.lastActiveAt ? new Date(user.lastActiveAt) : null;
      const inDateRange = (!from || (lastActive && lastActive >= from)) &&
        (!to || (lastActive && lastActive <= to));
      const isActive = lastActive && (now - lastActive) <= 24 * 60 * 60 * 1000;
      return (!search || searchable.includes(search)) &&
        (userCountry === 'all' || user.country === userCountry) &&
        (userRole === 'all' || user.gamingRole === userRole) &&
        (userActivity === 'all' || (userActivity === 'active' ? isActive : !isActive)) &&
        inDateRange;
    });
  }, [userStats, userPeriod, userSearch, userCountry, userRole, userActivity, dateFrom, dateTo]);

  const clearUserFilters = () => {
    setUserPeriod('all');
    setUserSearch('');
    setUserCountry('all');
    setUserRole('all');
    setUserActivity('all');
    setDateFrom('');
    setDateTo('');
  };

  return (
    <>
      <Helmet>
        <title> Dashboard | Trooper </title>
      </Helmet>

      <Container maxWidth="xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <AppWidgetSummary title="Total Products" total={metrics.products} color="info" icon={'mdi:package-variant'} />
            </Grid>
            <Grid item xs={12} md={3}>
              <AppWidgetSummary title="Total Orders" total={metrics.orders} color="warning" icon={'mdi:cart'} />
            </Grid>
            <Grid item xs={12} md={3}>
              <AppWidgetSummary title="Delivered Orders" total={metrics.byStatus?.Delivered || 0} color="success" icon={'mdi:truck-check'} />
            </Grid>
            <Grid item xs={12} md={3}>
              <AppWidgetSummary title="Pending Orders Today" total={metrics.pendingToday || 0} color="error" icon={'mdi:calendar-clock'} />
            </Grid>
          </Grid>
        </motion.div>
        <Box sx={{ mb: 3, mt: 1 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Daily" />
            <Tab label="Monthly" />
            <Tab label="Yearly" />
          </Tabs>
          {currentData && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <AppWidgetSummary title="Total Users" total={currentData.totalUsers} color="primary" icon={'mdi:account-group'} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <AppWidgetSummary title="Active Users (FCM)" total={currentData.hasFcmToken} color="info" icon={'mdi:cellphone-check'} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <AppWidgetSummary title={tab === 0 ? 'Active Today' : tab === 1 ? 'Active This Month' : 'Active This Year'}
                    total={tab === 0 ? currentData.activeToday : tab === 1 ? currentData.activeThisMonth : currentData.activeThisYear}
                    color="success" icon={'mdi:account-check'} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <AppWidgetSummary title={tab === 0 ? 'New Users Today' : tab === 1 ? 'New Users This Month' : 'New Users This Year'}
                    total={tab === 0 ? currentData.newUsersToday : tab === 1 ? currentData.newUsersThisMonth : currentData.newUsersThisYear}
                    color="warning" icon={'mdi:account-plus'} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <AppWidgetSummary title="Avg Daily Users" total={currentData.avgDailyUsers}
                    color="info" icon={'mdi:chart-line'} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <AppWidgetSummary title="Engagement Rate" total={`${currentData.engagementRate}%`}
                    color="secondary" icon={'mdi:trending-up'} />
                </Grid>
                {currentData.activeYesterday !== undefined && (
                  <Grid item xs={12} md={3}>
                    <AppWidgetSummary title="Active Yesterday" total={currentData.activeYesterday}
                      color="warning" icon={'mdi:calendar-yesterday'} />
                  </Grid>
                )}
              </Grid>
            </motion.div>
          )}
        </Box>
        {userStats.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2, color: 'text.primary' }}>User Activity & Rankings</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              <TextField
                size="small"
                label="Search users"
                value={userSearch}
                onChange={(event) => setUserSearch(event.target.value)}
                placeholder="Name, email, Trooper ID"
                sx={{ minWidth: 220 }}
              />
              <TextField select size="small" label="Data period" value={userPeriod}
                onChange={(event) => setUserPeriod(event.target.value)} sx={{ minWidth: 140 }}>
                <MenuItem value="all">All data</MenuItem>
                <MenuItem value="daily">Today</MenuItem>
                <MenuItem value="monthly">This month</MenuItem>
                <MenuItem value="yearly">This year</MenuItem>
                <MenuItem value="custom">Custom dates</MenuItem>
              </TextField>
              <TextField select size="small" label="Country" value={userCountry}
                onChange={(event) => setUserCountry(event.target.value)} sx={{ minWidth: 130 }}>
                <MenuItem value="all">All countries</MenuItem>
                {filterOptions.countries.map((country) => <MenuItem key={country} value={country}>{country}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Gaming role" value={userRole}
                onChange={(event) => setUserRole(event.target.value)} sx={{ minWidth: 145 }}>
                <MenuItem value="all">All roles</MenuItem>
                {filterOptions.roles.map((role) => <MenuItem key={role} value={role}>{role.replace(/_/g, ' ')}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Activity" value={userActivity}
                onChange={(event) => setUserActivity(event.target.value)} sx={{ minWidth: 125 }}>
                <MenuItem value="all">All users</MenuItem>
                <MenuItem value="active">Active today</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
              <Button variant="outlined" onClick={clearUserFilters}>Clear</Button>
            </Stack>
            {(userPeriod === 'custom' || dateFrom || dateTo) && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mb: 2 }}>
                <TextField size="small" type="date" label="From" value={dateFrom}
                  onChange={(event) => { setDateFrom(event.target.value); setUserPeriod('custom'); }}
                  InputLabelProps={{ shrink: true }} />
                <TextField size="small" type="date" label="To" value={dateTo}
                  onChange={(event) => { setDateTo(event.target.value); setUserPeriod('custom'); }}
                  InputLabelProps={{ shrink: true }} />
                <Typography variant="body2" sx={{ alignSelf: 'center', color: 'text.secondary' }}>
                  {filteredUserStats.length} matching users
                </Typography>
              </Stack>
            )}
            <div style={{ height: 450, minWidth: '100%', background: '#1e1e1e', borderRadius: 8 }}>
              <DataGrid
                rows={filteredUserStats}
                columns={[
                  { field: 'rank', headerName: '#', width: 50, align: 'center' },
                  { field: 'name', headerName: 'Name', width: 140 },
                  { field: 'email', headerName: 'Email', width: 200 },
                  { field: 'phone', headerName: 'Contact', width: 130 },
                  { field: 'trooperId', headerName: 'Trooper ID', width: 120 },
                  { field: 'country', headerName: 'Country', width: 100 },
                  { field: 'referralCode', headerName: 'Referral Code', width: 120 },
                  { field: 'referredBy', headerName: 'Invited By', width: 120 },
                  { field: 'totalReferred', headerName: 'Referred', width: 80, align: 'center' },
                  { field: 'gamingRole', headerName: 'Role', width: 120,
                    renderCell: (p) => p.value ? <Chip label={p.value.replace(/_/g, ' ')} size="small" variant="outlined" /> : '-'
                  },
                  { field: 'balanceTRP', headerName: 'TRP Coins', width: 100, align: 'center' },
                  { field: 'totalBalance', headerName: 'Total', width: 100, align: 'center' },
                  { field: 'lastActiveHoursAgo', headerName: 'Last Active', width: 130, align: 'center',
                    renderCell: (p) => {
                      if (p.value === null) return '—';
                      if (p.row.activeToday) return <Chip label="Today" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />;
                      return p.value < 24 ? `${p.value}h ago` : p.value < 48 ? 'Yesterday' : `${Math.round(p.value/24)}d ago`;
                    }
                  },
                ]}
                getRowId={(r) => r._id}
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                sx={{
                  '& .MuiDataGrid-cell': { color: '#e0e0e0' },
                  '& .MuiDataGrid-columnHeaders': { backgroundColor: '#2d2d2d', color: '#fff' },
                  border: 'none'
                }}
              />
            </div>
          </Box>
        )}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
          <Chart
            data={chartData}
            title={`Active Users — ${tab === 0 ? 'Last 7 Days' : 'Last 12 Months'}`}
            grid
            dataKey="activeUsers"
          />
        </motion.div>
      </Container>
    </>
  );
}
